import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Badge,
} from "reactstrap";
import axios from "axios";
import moment from "moment";
import { END_POINT } from "../../../constants/defaultValues";
import { Separator } from "../../../components/common/CustomBootstrap";
import FooterPage from "../../../containers/pages/FooterPage";
import IntlMessages from "../../../helpers/IntlMessages";
import Breadcrumb from "../../../containers/navs/Breadcrumb";
import { injectIntl } from "react-intl";
import {
  StockDisposalForm,
  StockDisposalList,
  StockDisposalApplicationMenu,
} from "../../../containers/pages/stock";

const StockDisposalPage = ({ match, intl }) => {
  const [stocks, setStocks] = useState([]);
  const [disposals, setDisposals] = useState([]);
  const [filteredDisposals, setFilteredDisposals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filter, setFilter] = useState(null);

  const loadStocks = async () => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/stock/all-unit`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (response.data && response.data.data) {
        setStocks(response.data.data);
      }
    } catch (error) {
      console.error("Error loading stocks:", error);
    }
  };

  const loadDisposals = async () => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/api/stock-disposal`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (response.data && response.data.data) {
        setDisposals(response.data.data);
        setFilteredDisposals(response.data.data);
      }
    } catch (error) {
      console.error("Error loading disposals:", error);
    }
  };

  const [providers, setProviders] = useState([]);
  const loadProviders = async () => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(`${END_POINT}/company-provider`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      });
      if (response.data && response.data.data) {
        setProviders(response.data.data);
      }
    } catch (error) {
      console.error("Error loading providers:", error);
    }
  };

  useEffect(() => {
    loadStocks();
    loadDisposals();
    loadProviders();
    // Check if user is admin
    const userId = localStorage.getItem("user_id");
    const idParent = localStorage.getItem("b");
    setIsAdmin(userId && idParent && userId === idParent);
  }, [refreshKey]);

  // Apply filter
  useEffect(() => {
    if (!filter || filter.type === "all") {
      setFilteredDisposals(disposals);
      return;
    }

    let filtered = [...disposals];

    if (filter.type === "status") {
      filtered = disposals.filter((item) => item.status === filter.value);
    } else if (filter.type === "disposal_type") {
      filtered = disposals.filter(
        (item) => item.disposal_type === filter.value,
      );
    } else if (filter.type === "month") {
      const startOfMonth = moment()
        .subtract(filter.value, "months")
        .startOf("month");
      const endOfMonth = moment()
        .subtract(filter.value, "months")
        .endOf("month");
      filtered = disposals.filter((item) => {
        const itemDate = moment(item.createdAt);
        return itemDate.isBetween(startOfMonth, endOfMonth, null, "[]");
      });
    }

    setFilteredDisposals(filtered);
  }, [filter, disposals]);

  const handleSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  // Detail Modal Logic
  const [modalDetailOpen, setModalDetailOpen] = useState(false);
  const [selectedDisposal, setSelectedDisposal] = useState(null);

  const handleViewDetail = async (disposal) => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.get(
        `${END_POINT}/api/stock-disposal/${disposal.id}`,
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      );
      if (response.data.status === 200) {
        setSelectedDisposal(response.data.data);
        setModalDetailOpen(true);
      }
    } catch (error) {
      console.error("Error loading disposal detail:", error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(value || 0);
  };

  return (
    <div className="app-row disable-text-selection">
      <div className="container-fluid">
        <Row className="mb-4">
          <Col>
            <Breadcrumb heading="menu.stock-disposal" match={match} />
            <p className="text-muted">
              <IntlMessages id="disposal.title" />
            </p>
          </Col>
          <Col className="text-right">
            <Button color="danger" onClick={() => setShowForm(true)}>
              <i className="simple-icon-plus mr-2"></i>
              <IntlMessages id="disposal.button.create" />
            </Button>
          </Col>
        </Row>

        <StockDisposalList
          data={filteredDisposals}
          key={refreshKey}
          isAdmin={isAdmin}
          onRefresh={handleSuccess}
          onViewDetail={handleViewDetail}
        />

        <StockDisposalForm
          isOpen={showForm}
          toggle={() => setShowForm(false)}
          stocks={stocks}
          providers={providers}
          onSuccess={handleSuccess}
        />

        {/* Detail Modal */}
        <Modal
          isOpen={modalDetailOpen}
          toggle={() => setModalDetailOpen(false)}
          size="lg"
          style={{ maxWidth: "90vw", width: "90vw" }}
        >
          <ModalHeader toggle={() => setModalDetailOpen(false)}>
            <IntlMessages id="disposal.modal.detail.title" /> -{" "}
            {selectedDisposal?.disposal_code}
          </ModalHeader>
          <ModalBody style={{ maxHeight: "80vh", overflowY: "auto" }}>
            {selectedDisposal && (
              <>
                <Row className="mb-4">
                  <Col sm="3">
                    <p className="mb-0">
                      <strong>
                        <IntlMessages id="disposal.date" />:{" "}
                      </strong>
                      {moment(selectedDisposal.disposal_date).format(
                        "DD/MM/YYYY",
                      )}
                    </p>
                  </Col>
                  <Col sm="3">
                    <p className="mb-0">
                      <strong>
                        <IntlMessages id="disposal.type" />:{" "}
                      </strong>
                      {selectedDisposal.disposal_type === "expired" ? (
                        <IntlMessages id="disposal.type.expired" />
                      ) : selectedDisposal.disposal_type === "damaged" ? (
                        <IntlMessages id="disposal.type.damaged" />
                      ) : (
                        <IntlMessages id="disposal.type.other" />
                      )}
                    </p>
                  </Col>
                  <Col sm="3">
                    <p className="mb-0">
                      <strong>
                        <IntlMessages id="disposal.status" />:{" "}
                      </strong>
                      {selectedDisposal.status === "completed" ? (
                        <Badge color="success">
                          <IntlMessages id="disposal.status.completed" />
                        </Badge>
                      ) : selectedDisposal.status === "pending" ? (
                        <Badge color="warning">
                          <IntlMessages id="disposal.status.pending" />
                        </Badge>
                      ) : (
                        <Badge color="danger">
                          <IntlMessages id="disposal.status.rejected" />
                        </Badge>
                      )}
                    </p>
                  </Col>
                  <Col sm="3">
                    <p className="mb-0">
                      <strong>
                        <IntlMessages id="audit.note" />:{" "}
                      </strong>
                      {selectedDisposal.notes || "Không có"}
                    </p>
                  </Col>
                </Row>

                <h5>Danh sách nguyên liệu</h5>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>
                        <IntlMessages id="disposal.table.stock" />
                      </th>
                      <th className="text-center">
                        <IntlMessages id="disposal.table.quantity" />
                      </th>
                      <th className="text-center">
                        <IntlMessages id="disposal.table.unit" />
                      </th>
                      <th className="text-center">
                        <IntlMessages id="disposal.table.provider" />
                      </th>
                      <th className="text-center">
                        <IntlMessages id="disposal.table.refundable" />
                      </th>
                      <th className="text-right">
                        <IntlMessages id="disposal.table.price" />
                      </th>
                      <th className="text-right">
                        <IntlMessages id="disposal.table.total" />
                      </th>
                      <th>
                        <IntlMessages id="disposal.table.reason" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDisposal.items &&
                      selectedDisposal.items.map((item) => (
                        <tr key={item.id}>
                          <td>{item.stock?.name}</td>
                          <td className="text-center">{item.quantity}</td>
                          <td className="text-center">{item.stock?.unit}</td>
                          <td className="text-center">
                            {item.provider ? item.provider.name : "-"}
                          </td>
                          <td className="text-center">
                            {item.is_refundable ? (
                              <Badge color="success">
                                <IntlMessages id="disposal.refundable.yes" />
                              </Badge>
                            ) : (
                              <Badge color="secondary">
                                <IntlMessages id="disposal.refundable.no" />
                              </Badge>
                            )}
                          </td>
                          <td className="text-right">
                            {formatCurrency(item.unit_price)}
                          </td>
                          <td className="text-right">
                            {formatCurrency(item.quantity * item.unit_price)}
                          </td>
                          <td>{item.reason}</td>
                        </tr>
                      ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="6" className="text-right">
                        <strong>
                          <IntlMessages id="disposal.total" />:
                        </strong>
                      </td>
                      <td className="text-right text-danger">
                        <strong>
                          {formatCurrency(selectedDisposal.total_value)}
                        </strong>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </Table>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalDetailOpen(false)}>
              <IntlMessages id="disposal.button.close" />
            </Button>
          </ModalFooter>
        </Modal>
      </div>

      <StockDisposalApplicationMenu
        data={disposals}
        filter={filter}
        onFilterChange={handleFilterChange}
        isAdmin={isAdmin}
      />

      <Separator />
      <FooterPage />
    </div>
  );
};

export default injectIntl(StockDisposalPage);
